import React, { useEffect, useState } from "react";
import { Sparkles, Plus, Trash2, Pencil, X, Check, RotateCcw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = [
    { key: "lucht", label: "Lucht" },
    { key: "objecten", label: "Objecten verwijderen" },
    { key: "interieur", label: "Interieur" },
    { key: "tuin", label: "Tuin / buiten" },
    { key: "licht", label: "Licht / sfeer" },
    { key: "overig", label: "Overig" },
];

const EMPTY_FORM = {
    id: null,
    label_nl: "",
    category: "lucht",
    prompt_nl: "",
};

/**
 * Admin-only manager for the Nano Banana AI photo presets.
 * Lives in /admin/settings under "Producten & prijzen".
 * Each preset = (label, category, prompt_nl). The Dutch prompt is dropped
 * verbatim into the admin lightbox textarea, then Claude translates it to
 * English before sending to Fal nano-banana-pro/edit.
 */
export default function AiPhotoPresetsManager() {
    const [presets, setPresets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [armedDelete, setArmedDelete] = useState(null);
    const [resetting, setResetting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/ai-photo-presets");
            setPresets(data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Laden mislukt");
        }
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    useEffect(() => {
        if (!armedDelete) return;
        const t = setTimeout(() => setArmedDelete(null), 4000);
        return () => clearTimeout(t);
    }, [armedDelete]);

    const openNew = () => setEditing({ ...EMPTY_FORM });
    const openEdit = (p) => setEditing({ ...p });
    const cancelEdit = () => setEditing(null);

    const save = async () => {
        if (!editing) return;
        const label = (editing.label_nl || "").trim();
        const prompt = (editing.prompt_nl || "").trim();
        if (label.length < 3) { toast.error("Label is te kort"); return; }
        if (prompt.length < 5) { toast.error("Prompt is te kort (min 5 tekens)"); return; }
        setSaving(true);
        try {
            if (editing.id) {
                const { data } = await api.patch(`/admin/ai-photo-presets/${editing.id}`, {
                    label_nl: label,
                    category: editing.category,
                    prompt_nl: prompt,
                });
                setPresets((prev) => prev.map((p) => p.id === data.id ? data : p));
                toast.success("Preset bijgewerkt");
            } else {
                const { data } = await api.post("/admin/ai-photo-presets", {
                    label_nl: label,
                    category: editing.category,
                    prompt_nl: prompt,
                });
                setPresets((prev) => [...prev, data]);
                toast.success("Preset aangemaakt");
            }
            setEditing(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Opslaan mislukt");
        }
        setSaving(false);
    };

    const remove = async (preset) => {
        if (armedDelete !== preset.id) {
            setArmedDelete(preset.id);
            return;
        }
        setArmedDelete(null);
        try {
            await api.delete(`/admin/ai-photo-presets/${preset.id}`);
            setPresets((prev) => prev.filter((p) => p.id !== preset.id));
            toast.success("Preset verwijderd");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Verwijderen mislukt");
        }
    };

    const resetBuiltins = async () => {
        setResetting(true);
        try {
            await api.post("/admin/ai-photo-presets/reset-defaults");
            await load();
            toast.success("Standaardpresets hersteld");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Herstellen mislukt");
        }
        setResetting(false);
    };

    const groups = CATEGORIES.map((c) => ({
        ...c,
        items: presets.filter((p) => p.category === c.key),
    }));

    return (
        <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8" data-testid="ai-photo-presets-section">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-[#E5A93D]" />
                    <h2 className="font-display font-bold text-xl tracking-tight">AI foto presets (Nano Banana)</h2>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={resetBuiltins}
                        disabled={resetting}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm text-xs font-medium bg-zinc-100 hover:bg-zinc-100 border border-zinc-200 text-zinc-600 active:scale-[0.98] transition disabled:opacity-60"
                        data-testid="reset-ai-photo-presets-btn"
                        title="Herstelt de fabrieks-presets (custom presets blijven behouden)"
                    >
                        {resetting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                        Standaard herstellen
                    </button>
                    <button
                        type="button"
                        onClick={openNew}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm text-xs font-medium bg-[#E5A93D] text-black hover:bg-[#C8922C] active:scale-[0.98] transition"
                        data-testid="new-ai-photo-preset-btn"
                    >
                        <Plus className="h-4 w-4" /> Nieuwe preset
                    </button>
                </div>
            </div>
            <p className="text-xs text-zinc-500 mb-6">
                Beheer de Nederlandse prompt-templates die in de admin lightbox als dropdown verschijnen.
                De prompt wordt door <span className="text-[#E5A93D] font-medium">Claude vertaald naar Engels</span> en daarna naar Fal Nano Banana Pro gestuurd.
            </p>

            {loading && (
                <div className="text-sm text-zinc-500 py-6 text-center">Laden…</div>
            )}

            {!loading && presets.length === 0 && (
                <div className="text-sm text-zinc-500 py-6 text-center">Geen presets — klik "Standaard herstellen" of "Nieuwe preset".</div>
            )}

            {!loading && groups.map((g) => g.items.length > 0 && (
                <div key={g.key} className="mb-5">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{g.label}</div>
                    <div className="space-y-2">
                        {g.items.map((p) => (
                            <div
                                key={p.id}
                                className="flex items-start gap-3 p-3 bg-[#F4F4F5] border border-zinc-200 rounded-sm hover:border-zinc-400"
                                data-testid={`ai-photo-preset-row-${p.id}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-zinc-950 font-medium">{p.label_nl}</span>
                                        {p.builtin && (
                                            <span className="text-[9px] uppercase tracking-wider bg-zinc-100 border border-zinc-200 rounded-sm px-1.5 py-0.5 text-zinc-500">Standaard</span>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-zinc-500 mt-1 font-mono leading-snug line-clamp-2">
                                        {p.prompt_nl}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => openEdit(p)}
                                    className="h-9 w-9 inline-flex items-center justify-center rounded-sm border border-zinc-200 hover:bg-zinc-100 text-zinc-600"
                                    aria-label="Bewerken"
                                    data-testid={`edit-ai-photo-preset-${p.id}`}
                                >
                                    <Pencil className="h-3.5 w-3.5" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => remove(p)}
                                    className={`h-9 inline-flex items-center gap-1.5 px-3 rounded-sm transition active:scale-95 border text-xs ${armedDelete === p.id ? "bg-red-500 hover:bg-red-400 text-zinc-950 border-red-400 font-bold" : "border-red-500/30 text-red-600 hover:bg-red-500/10"}`}
                                    aria-label="Verwijderen"
                                    data-testid={`delete-ai-photo-preset-${p.id}`}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                    {armedDelete === p.id ? "Klik nogmaals" : ""}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            {editing && (
                <div
                    className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-auto"
                    onClick={(e) => { if (e.target === e.currentTarget) cancelEdit(); }}
                    data-testid="ai-photo-preset-modal"
                >
                    <div className="bg-[#FFFFFF] border border-[#E5A93D]/40 rounded-md max-w-2xl w-full max-h-[90vh] overflow-auto scrollbar-thin shadow-2xl shadow-black/60">
                        <div className="p-6 lg:p-8 space-y-5">
                            <div className="flex items-center justify-between gap-3">
                                <h3 className="font-display font-bold text-xl tracking-tight">
                                    {editing.id ? `Bewerk preset` : "Nieuwe preset"}
                                </h3>
                                <button onClick={cancelEdit} className="h-9 w-9 inline-flex items-center justify-center rounded-sm bg-zinc-100 hover:bg-zinc-200 text-zinc-950">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>

                            <div className="space-y-1">
                                <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Naam (Nederlands)</Label>
                                <Input
                                    value={editing.label_nl}
                                    onChange={(e) => setEditing((s) => ({ ...s, label_nl: e.target.value }))}
                                    placeholder="bv. Lucht · Blauwe zomerlucht"
                                    className="bg-[#F4F4F5] border-zinc-200 rounded-sm h-11 focus-visible:border-[#E5A93D]"
                                    data-testid="ai-photo-preset-label"
                                />
                            </div>

                            <div className="space-y-1">
                                <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Categorie</Label>
                                <select
                                    value={editing.category}
                                    onChange={(e) => setEditing((s) => ({ ...s, category: e.target.value }))}
                                    className="w-full bg-[#F4F4F5] border border-zinc-200 rounded-sm h-11 px-3 text-sm text-zinc-800 focus:outline-none focus:border-[#E5A93D]"
                                    data-testid="ai-photo-preset-category"
                                >
                                    {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                                </select>
                            </div>

                            <div className="space-y-1">
                                <div className="flex items-center justify-between gap-2">
                                    <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Prompt (Nederlands — wordt vertaald door Claude)</Label>
                                    <span className="text-[10px] text-zinc-500 font-mono">{(editing.prompt_nl || "").length} tekens</span>
                                </div>
                                <textarea
                                    rows={6}
                                    value={editing.prompt_nl}
                                    onChange={(e) => setEditing((s) => ({ ...s, prompt_nl: e.target.value }))}
                                    placeholder="bv. Vervang de lucht door een heldere blauwe zomerlucht. Behoud de bestaande belichting en schaduwen."
                                    className="w-full bg-[#F4F4F5] border border-zinc-200 rounded-sm p-3 text-sm text-zinc-800 focus:outline-none focus:border-[#E5A93D] font-mono leading-snug"
                                    data-testid="ai-photo-preset-prompt"
                                />
                                <p className="text-[10px] text-zinc-500">
                                    Tip: wees concreet — noem het object/onderwerp, gewenst resultaat én "behoud schaduwen/belichting natuurlijk".
                                </p>
                            </div>

                            <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100">
                                <button
                                    type="button"
                                    onClick={cancelEdit}
                                    className="h-9 px-4 rounded-sm bg-zinc-100 hover:bg-zinc-100 border border-zinc-200 text-xs text-zinc-600"
                                    data-testid="ai-photo-preset-cancel"
                                >
                                    Annuleer
                                </button>
                                <Button
                                    onClick={save}
                                    disabled={saving}
                                    className="h-9 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98]"
                                    data-testid="ai-photo-preset-save"
                                >
                                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}
                                    {editing.id ? "Opslaan" : "Aanmaken"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
